import {
  ApiButton,
  Box,
  Button,
  Checkbox,
  ContentTemplate,
  CustomSelect,
  DatePicker,
  ErrorInfo,
  HeaderInput,
  HeaderTemplate,
  KeyValue, PAYLOAD_DATE_FORMAT,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { FormControlLabel, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import parse from 'html-react-parser';
import {
  generateAuthorityName,
  NOTIFICATION_FUNCTION_MAP,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import dayjs from 'dayjs';
import { configurationTypeData } from '../../../modules/admin/logic/dropDownOption';
import { useSendNotification } from './useSendNotification';
import RouteKey from '../../../routes/key';
import RecipientContent from './components/RecipientContent';
import { NotificationSearchRecipientResponseContentObject } from '../../../api-refactored/manage-notifications/usePostNotifcationSearchRecipient';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';

const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
  notificationDetailsContainer: {
    marginBottom: theme.spacing(4),
  },
  formControlLabel: {
    marginBottom: theme.spacing(3),
  },
  requiredLabel: {
    display: 'inline-block',
    marginLeft: theme.spacing(0.5),
    color: theme.color.state.stateImportant,
  },
  radioContainer: {
    marginBottom: theme.spacing(4),
  },
  keyLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.color.text.textHeader,
  },
  dropDownContainer: {
    marginBottom: theme.spacing(4),
  },
  lastDropDownContainer: {
    marginBottom: theme.spacing(9),
  },
  halfWidthLeftContainer: {
    width: '50%',
    marginBottom: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 25px 0 16px !important',
  },
  arrowIconContainer: {
    right: '-10px',
  },
  disabledRadioButton: {
    color: theme.color.text.textDisabled,
    pointerEvents: 'none',
  },
  recipientListContainer: {
    marginTop: theme.spacing(5),
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  customSelectError: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 25px 0 16px !important',
    backgroundColor: theme.color.state.stateErrorLight,
    borderColor: theme.color.state.stateError,
    '&:focus': {
      backgroundColor: theme.color.state.stateErrorLight,
      borderColor: theme.color.state.stateError,
      boxShadow: 'none',
    },
  },
}));

export enum RecipientSelectionMode {
  Everyone = 'Everyone',
  SelectedRecipients = 'SelectedRecipients',
}

export enum RecipientTypeMode {
  Candidate = 'Candidate',
  Coordinator = 'Coordinator',
}

export enum SelectRecipientByMode {
  UserGroup = 'User Group',
  Individual = 'Individual',
}

const { admin } = RouteKey.child;
const { manageNotifications } = RouteKey.admin.child;

const getFeatureLabel = (feature: string) => {
  if (feature === 'EMAIL') return 'Email';
  if (feature === 'SMS') return 'SMS';
  if (feature === 'IN_APP') return 'In-App';
  return null;
};

const getPermissionType = (isEdit: boolean) =>
  isEdit ? PermissionType.EDIT : PermissionType.CREATE;

const currentDate = dayjs();

interface SendNotificationProps {
  isEdit: boolean;
}

const SendNotification = ({ isEdit }: SendNotificationProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory<{
    featureValue: string;
    defaultTab: number;
    page: number;
    pageSize: number;
  }>();

  const dataFromListing = history?.location?.state;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [featureValue, setFeatureValue] = useState<string>(dataFromListing?.featureValue || '');
  const [examLevelValue, setExamLevelValue] = useState<string[]>([]);

  const [candidateTypeValue, setCandidateTypeValue] = useState<string[]>([]);

  const [schoolValue, setSchoolValue] = useState<string[]>([]);

  const [recipientTypeMode, setRecipientTypeMode] = useState<RecipientTypeMode>(
    RecipientTypeMode.Candidate
  );

  const [selectRecipientByMode, setSelectRecipientByMode] = useState<SelectRecipientByMode>(
    SelectRecipientByMode.UserGroup
  );

  const [acknowledgementCheckbox, setAcknowledgementCheckbox] = useState<boolean>(false);

  const [sendDate, setSendDate] = useState<string>(currentDate.format(PAYLOAD_DATE_FORMAT));

  const [displayError, setDisplayError] = useState<boolean>(false);

  const [sendNotificationError, setSendNotificationError] = useState<string | undefined>("");

  const [selectedCandidates, setSelectedCandidates] = useState<
    NotificationSearchRecipientResponseContentObject[]
  >([]);

  const authorities = useGetUserAuthorities();

  const {
    notificationTemplateDetails,
    notificationDetails,
    notificationFeature,
    statusCreateNotification,
    statusEditNotification,
    handleCreateNotification,
    notificationSchools,
    notificationExamLevels,
    notificationCandidateTypes,
    isLoadingProgressIndicator,
  } = useSendNotification({
    featureValue,
    examLevelValue,
    candidateTypeValue,
    schoolValue,
    isEdit,
    acknowledgementCheckbox,
    sendDate,
    recipientTypeMode,
    selectRecipientByMode,
    selectedCandidates,
    setAcknowledgementCheckbox,
    setExamLevelValue,
    setCandidateTypeValue,
    setSchoolValue,
    setSendDate,
    setFeatureValue,
    setSelectRecipientByMode,
    setRecipientTypeMode,
    setSelectedCandidates,
    sendNotificationError,
    setSendNotificationError
  });

  const detailsToRender = useMemo(() => {
    if (isEdit) {
      return notificationDetails;
    }
    return notificationTemplateDetails;
  }, [notificationDetails, notificationTemplateDetails, isEdit]);

  useEffect(() => {
    if (recipientTypeMode === RecipientTypeMode.Coordinator) {
      setSelectRecipientByMode(SelectRecipientByMode.UserGroup);
    }
  }, [recipientTypeMode]);

  if (isLoadingProgressIndicator) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const handleAcknowledgementCheckbox = () => setAcknowledgementCheckbox(!acknowledgementCheckbox);

  const handleCancel = () =>
    history.push(`/${admin}/${manageNotifications.route}`, {
      featureValue: dataFromListing.featureValue,
      defaultTab: dataFromListing.defaultTab,
      page: dataFromListing.page,
      pageSize: dataFromListing.pageSize,
    });

  return (
    <>
      <TitleHeader title={t('announcementsNotifications->>Listing.ManageNotifications')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('announcementsNotifications->>Listing.Feature')}
          input={
            notificationFeature &&
            configurationTypeData(notificationFeature) && (
              <CustomSelect
                id="inputSelect-feature"
                isHeader
                menuItems={configurationTypeData(notificationFeature)}
                value={featureValue}
                className={classes.select}
                disabled
              />
            )
          }
          topBottom={false}
        />
      </HeaderTemplate>
      <ContentTemplate isDividerBottom isContinuable>
        <SectionHeader
          title={t('announcementsNotifications->>SendNotification.SendFeatureNotifications', {
            feature: getFeatureLabel(featureValue),
          })} />
        {detailsToRender && (
          <Box className={classes.notificationDetailsContainer}>
            <Typography className={classes.sectionTitle} variant="h3">
              {t('announcementsNotifications->>SendNotification.NotificationDetails')}
            </Typography>
            <KeyValue
              keyText={t('announcementsNotifications->>SendNotification.Title')}
              valueText={
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {detailsToRender?.title || '-'}
                </Typography>
              }
            />
            {featureValue !== 'SMS' && (
              <KeyValue
                keyText={t('announcementsNotifications->>SendNotification.Message')}
                valueText={<>{parse(detailsToRender?.message || '-')}</>}
              />
            )}
            {featureValue === 'EMAIL' && (
              <FormControlLabel
                className={classes.formControlLabel}
                name="acknowledgmentCheckbox"
                control={<Checkbox defaultChecked={acknowledgementCheckbox} />}
                label={t('announcementsNotifications->>SendNotification.CheckboxStatement')}
                onChange={handleAcknowledgementCheckbox}
              />
            )}
          </Box>
        )}
      </ContentTemplate>
      <RecipientContent
        classes={classes}
        selectRecipientByMode={selectRecipientByMode}
        recipientTypeMode={recipientTypeMode}
        setRecipientTypeMode={setRecipientTypeMode}
        setSelectRecipientByMode={setSelectRecipientByMode}
        notificationCandidateTypes={notificationCandidateTypes}
        notificationExamLevels={notificationExamLevels}
        examLevelValue={examLevelValue}
        displayError={displayError}
        setExamLevelValue={setExamLevelValue}
        candidateTypeValue={candidateTypeValue}
        setCandidateTypeValue={setCandidateTypeValue}
        notificationSchools={notificationSchools}
        schoolValue={schoolValue}
        setSchoolValue={setSchoolValue}
        featureValue={featureValue}
        selectedCandidates={selectedCandidates}
        setSelectedCandidates={setSelectedCandidates}
      />
      <ContentTemplate continuous>
        <Typography className={classes.sectionTitle} variant="h3">
          {t('announcementsNotifications->>SendNotification.ScheduleEmail')}
        </Typography>
        <Box className={classes.halfWidthLeftContainer}>
          <DatePicker
            placeholder="Select send date"
            value={dayjs(sendDate, PAYLOAD_DATE_FORMAT)}
            label={t('announcementsNotifications->>SendNotification.SendDate')}
            // labelFunc={() => stringToSemanticDisplay(sendDate)}
            onChange={(date) => {
              setSendDate(date || '');
            }}
            inputVariant="filled"
            name="sendDate"
            disablePast
            isRequired
          />
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            id="button-send-email-notification--cancel"
            className={classes.cancelButton}
            variant="outlined"
            fullWidth={isMobile}
            onClick={handleCancel}
          >
            {t('announcementsNotifications->>SendNotification.Cancel')}
          </Button>

          {authorities.data?.includes(
            generateAuthorityName(
              'CP',
              FunctionModule.ADMIN,
              NOTIFICATION_FUNCTION_MAP[featureValue],
              getPermissionType(isEdit)
            )
          ) && (
              <>
                <ApiButton
                  id="button-send-email-notification-send-notification"
                  type="submit"
                  status={isEdit ? statusEditNotification : statusCreateNotification}
                  fullWidth={isMobile}
                  onClick={() => {
                    setDisplayError(true);
                    setSendNotificationError("");
                    handleCreateNotification();
                  }}
                >
                  {t('announcementsNotifications->>SendNotification.SendNotification')}
                </ApiButton>
                {sendNotificationError &&
                  <ErrorInfo label={sendNotificationError || ""} />
                }
              </>
            )}
        </Box>
      </ContentTemplate>
    </>
  );
};

export default SendNotification;
